/**
 * Workload Tracking Validation Service - Phase 4 Architecture
 *
 * Encapsulates workload entry validation and data quality checks.
 * Extracted from WorkloadTrackingService to reduce single-responsibility violations.
 */

import { logger } from "@/lib/logger";
import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from "@/services/core/service-registry";
import type { WorkloadTracking } from "@/types/time-management";

export class WorkloadTrackingValidationService implements IService {
  public readonly name = "WorkloadTrackingValidationService";
  private readonly PRODUCTIVITY_SCORE_RANGE = { min: 0, max: 10 };

  async initialize(): Promise<void> {
    logger.info("✅ WorkloadTrackingValidationService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("✅ WorkloadTrackingValidationService disposed");
  }

  async validateWorkloadData(
    entry: WorkloadTracking
  ): Promise<ServiceResponse<{ isValid: boolean; warnings: string[] }>> {
    try {
      const warnings: string[] = [];
      let isValid = true;

      if (!entry.date || isNaN(Date.parse(entry.date))) {
        warnings.push("Invalid date format");
        isValid = false;
      }

      if (entry.planned_hours < 0 || entry.planned_hours > 24) {
        warnings.push("Planned hours should be between 0 and 24");
        isValid = false;
      }

      if (entry.actual_hours < 0 || entry.actual_hours > 24) {
        warnings.push("Actual hours should be between 0 and 24");
        isValid = false;
      }

      if (entry.task_count < 0 || entry.completed_tasks < 0) {
        warnings.push("Task counts cannot be negative");
        isValid = false;
      }

      if (entry.completed_tasks > entry.task_count) {
        warnings.push("Completed tasks cannot exceed total task count");
        isValid = false;
      }

      if (
        entry.productivity_score !== null &&
        entry.productivity_score !== undefined
      ) {
        if (
          entry.productivity_score < this.PRODUCTIVITY_SCORE_RANGE.min ||
          entry.productivity_score > this.PRODUCTIVITY_SCORE_RANGE.max
        ) {
          warnings.push(
            `Productivity score should be between ${this.PRODUCTIVITY_SCORE_RANGE.min} and ${this.PRODUCTIVITY_SCORE_RANGE.max}`
          );
          isValid = false;
        }
      }

      if (
        entry.focus_time_minutes < 0 ||
        entry.focus_time_minutes > entry.actual_hours * 60
      ) {
        warnings.push("Focus time cannot exceed actual work hours");
        isValid = false;
      }

      if (entry.interruption_count < 0) {
        warnings.push("Interruption count cannot be negative");
        isValid = false;
      }

      if (entry.actual_hours > entry.planned_hours * 1.5) {
        warnings.push(
          "Actual hours significantly exceed planned hours - consider better planning"
        );
      }

      if (entry.interruption_count > entry.actual_hours * 10) {
        warnings.push("Very high interruption count detected");
      }

      return createSuccessResponse({ isValid, warnings });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }
}
